<script>
  // ══════════════════════════════════════════════════════════════════════════
  // ИМУЩЕСТВО: Транспорт · Хранилище · Недвижимость — перенос property.tsx 1:1.
  // «Мастер-деталь»: слева сетка фотографий, справа карточка выбранного.
  // ══════════════════════════════════════════════════════════════════════════
  import { t } from '../../i18n.js';
  import { money, num, dmy } from '../partsfn.js';
  import { vehiclePhoto, itemPhoto, clothesPhoto, parseClothesData, propertyPhoto, warehousePhoto, HOUSE_PHOTO } from '../photofn.js';
  import { ITEM_NAMES, ITEM_DESC } from '../demo.js';
  import Ico from '../ico.svelte';
  import Empty from '../empty.svelte';
  import Photo from '../photo.svelte';
  import Scroll from '../ui/scroll.svelte';
  import Sechead from '../ui/sechead.svelte';
  import Panel from '../ui/panel.svelte';
  import Metric from '../ui/metric.svelte';
  import Mgrid from '../ui/mgrid.svelte';
  import Prog from '../ui/prog.svelte';
  import Chip from '../ui/chip.svelte';
  import GoldBtn from '../ui/goldbtn.svelte';
  import GhostBtn from '../ui/ghostbtn.svelte';
  import Rows from '../ui/rows.svelte';
  import Line from '../ui/line.svelte';
  import Grid from '../ui/grid.svelte';
  import PhotoCard from '../ui/photocard.svelte';
  import Search from '../ui/search.svelte';

  export let part = 'vehicles';
  export let vehs = [];
  export let items = [];
  export let estate = {};
  export let nui = () => {};

  const VEH_STATE = {
    garage: { key: 'm.veh.garage', icon: 'home' },
    street: { key: 'm.veh.street', icon: 'road' },
    impound: { key: 'm.veh.impound', icon: 'alert' },
  };

  // ── Транспорт
  let sel = 0;
  $: v = vehs[sel];

  // ── Хранилище
  let ssel = 0;
  let q = '';
  const nameOf = (it) => it.Name || ITEM_NAMES[it.ItemId] || ('#' + it.ItemId);
  $: list = (() => {
    const n = q.trim().toLowerCase();
    return n ? items.filter((it) => nameOf(it).toLowerCase().includes(n)) : items;
  })();
  $: sit = list[Math.min(ssel, list.length - 1)];

  /** Фото предмета: одежда резолвится по слоту+Data, остальное — по нашему ItemId. */
  const stockPhoto = (it) => {
    const slot = it.slot ?? (it.ItemId < 0 ? it.ItemId : 0);
    if (slot < 0) {
      const d = parseClothesData(it.Data);
      return [clothesPhoto(slot, d.drawable, d.texture, d.male), itemPhoto(Math.abs(slot))].filter(Boolean);
    }
    return [itemPhoto(it.ItemId)];
  };

  // ── Недвижимость
  $: house = estate.house;
  $: biz = estate.biz;
  $: warehouse = estate.warehouse;
  $: nothing = !house && !biz && !warehouse;
</script>

{#if part === 'vehicles'}
  {#if vehs.length === 0}
    <Scroll>
      <Sechead kicker={t('m.veh.kicker')} title={t('m.veh.title')} />
      <Panel><Empty icon="vehicles" text={t('m.veh.none')} /></Panel>
    </Scroll>
  {:else}
    <div class="mn-fill">
      <div class="head">
        <Sechead kicker={t('m.veh.kicker')} title={t('m.veh.title')}>
          <svelte:fragment slot="meta">{t('m.total')}: <b>{vehs.length}</b></svelte:fragment>
        </Sechead>
      </div>
      <div class="mn-split grow">
        <div class="r">
          <Grid size="wide">
            {#each vehs as car, i (car.model + i)}
              {@const st = VEH_STATE[car.state || 'garage']}
              <PhotoCard
                src={vehiclePhoto(car.model)} title={car.name || car.model}
                sub={car.number} active={i === sel} on:click={() => (sel = i)} shotCls="wide" icon="vehicles"
              >
                <svelte:fragment slot="foot">
                  <span
                    class="mn-ico-txt"
                    style="font:700 10px var(--f-body);letter-spacing:0.12em;text-transform:uppercase;color:{car.state === 'impound' ? 'var(--neg)' : 'var(--ink-mute)'}"
                  ><Ico n={st.icon} s={13} />{t(st.key)}</span>
                </svelte:fragment>
              </PhotoCard>
            {/each}
          </Grid>
        </div>

        {#if v}
          <div class="l">
            <Panel>
              <Photo src={vehiclePhoto(v.model)} alt={v.name} cls="tall" icon="vehicles" />
              <Sechead title={v.name || v.model} divider={false}>
                <svelte:fragment slot="meta"><Chip tone="gold">{v.number}</Chip></svelte:fragment>
              </Sechead>
              <div class="ui-field">
                <div class="labrow">
                  <span class="lab mn-ico-txt" style="margin-bottom:0"><Ico n="fuel" s={14} />{t('m.veh.fuel')}</span>
                  <span class="note mn-num">{num(v.fuel)}%</span>
                </div>
                <Prog value={v.fuel ?? 0} />
              </div>
              <div class="ui-field">
                <div class="labrow">
                  <span class="lab mn-ico-txt" style="margin-bottom:0"><Ico n="gauge" s={14} />{t('m.veh.state')}</span>
                  <span class="note mn-num">{num(v.health)}%</span>
                </div>
                <Prog value={v.health ?? 0} />
              </div>
              <Rows>
                <Line k={t('m.veh.mileage')} v={`${num(v.mileage)} км`} />
                <Line k={t('m.veh.parking')} v={v.parking || '—'} />
              </Rows>
              <div class="mn-acts">
                <GhostBtn block size="sm" on:click={() => nui('vehLocate', { model: v.model, plate: v.number })}>
                  <Ico n="pin" s={15} />{t('m.veh.locate')}
                </GhostBtn>
                <!-- эвакуация: доступна, когда машина брошена на улице или на штрафстоянке —
                     в гараже эвакуировать нечего (проверка та же, что на сервере) -->
                {#if v.state !== 'garage'}
                  <GhostBtn block size="sm" on:click={() => nui('vehEvac', { plate: v.number })}>
                    <Ico n="vehicles" s={15} />{t('m.veh.evac')}
                  </GhostBtn>
                {/if}
                {#if v.state === 'impound'}
                  <GhostBtn block size="sm" on:click={() => nui('vehImpoundPay', { plate: v.number })}>
                    <Ico n="ticket" s={15} />{t('m.veh.impound')}
                  </GhostBtn>
                {/if}
                <GhostBtn block size="sm" on:click={() => nui('vehShare', { plate: v.number })}>
                  <Ico n="key" s={15} />{t('m.veh.keys')}
                </GhostBtn>
              </div>
            </Panel>
          </div>
        {/if}
      </div>
    </div>
  {/if}

{:else if part === 'storage'}
  <div class="mn-fill">
    <div class="head">
      <Sechead kicker={t('m.stg.kicker')} title={t('m.stg.title')}>
        <svelte:fragment slot="meta"><b>{items.length}</b> {t('m.stg.slots')}</svelte:fragment>
      </Sechead>
      {#if items.length > 0}
        <div class="mn-tools">
          <div class="grow" style="max-width:360px"><Search value={q} onChange={(val) => { q = val; ssel = 0; }} /></div>
        </div>
      {/if}
    </div>
    {#if list.length === 0}
      <Panel><Empty icon="storage" text={q ? t('m.nothingFound') : t('m.stg.none')} /></Panel>
    {:else}
      <div class="mn-split grow">
        <div class="r">
          <Grid>
            {#each list as item, i ((item.SqlId ?? i) + '-' + item.ItemId)}
              <PhotoCard
                src={stockPhoto(item)} title={nameOf(item)}
                count={(item.Count ?? 1) > 1 ? item.Count : undefined}
                active={i === ssel} on:click={() => (ssel = i)} icon="storage"
              >
                <svelte:fragment slot="foot"><span class="sub">{num(item.Count ?? 1)} {t('m.stg.count')}</span></svelte:fragment>
              </PhotoCard>
            {/each}
          </Grid>
        </div>
        {#if sit}
          <div class="l">
            <Panel>
              <Photo src={stockPhoto(sit)} alt={sit.Name} cls="tall" icon="storage" />
              <Sechead title={nameOf(sit)} divider={false}>
                <svelte:fragment slot="meta"><Chip tone="gold">{num(sit.Count ?? 1)} {t('m.stg.count')}</Chip></svelte:fragment>
              </Sechead>
              <div class="hint" style="margin-bottom:12px">{sit.Description || ITEM_DESC[sit.ItemId] || ''}</div>
              <Rows>
                <Line k={t('m.stg.count')} v={num(sit.Count ?? 1)} tone="gold" />
              </Rows>
              <div class="mn-acts">
                <!-- Вещи со склада выдают НА МЕСТЕ — в отделении на Инносент-бульваре.
                     Кнопка «забрать в инвентарь» вводила в заблуждение: удалённо предмет
                     получить нельзя, поэтому ставим метку и ведём игрока к стойке. -->
                <div class="hint" style="margin-bottom:10px">{t('m.stg.pickupHint')}</div>
                <GoldBtn block size="sm" on:click={() => nui('mapRoute', { x: 83.07, y: 133.79, label: t('m.stg.point') })}>
                  <Ico n="pin" s={15} />{t('m.stg.route')}
                </GoldBtn>
              </div>
            </Panel>
          </div>
        {/if}
      </div>
    {/if}
  </div>

{:else}
  <Scroll>
    <Sechead kicker={t('m.es.kicker')} title={t('m.es.title')}>
      <svelte:fragment slot="meta">{t('m.es.safe')}: <b>{money((house?.cash ?? 0) + (biz?.cash ?? 0))}</b></svelte:fragment>
    </Sechead>
    {#if nothing}
      <Panel><Empty icon="estate" text={t('m.es.none')} /></Panel>
    {:else}
      <div class="mn-grid wide">
        {#if house}
          <Panel>
            <Photo src={HOUSE_PHOTO} alt={t('m.es.house')} cls="tall photo" icon="home" />
            <Sechead title={house.type} divider={false}>
              <svelte:fragment slot="meta"><Chip tone="gold">{t('m.es.house')}</Chip></svelte:fragment>
            </Sechead>
            <Rows>
              <Line k={t('m.es.addr')} v={house.addr} />
              <Line k={t('m.es.safe')} v={money(house.cash)} tone="gold" />
              <Line k={t('m.es.slots')} v={num(house.maxcars)} />
              <Line k={t('m.es.income')} v={`${money(house.copiesHour)} / ч`} />
            </Rows>
          </Panel>
        {/if}
        {#if biz}
          <Panel>
            <Photo src={propertyPhoto(biz.kind, biz.photo)} alt={biz.name} cls="tall photo" icon="store" />
            <Sechead title={biz.name} divider={false}>
              <svelte:fragment slot="meta"><Chip tone="gold">{t('m.es.biz')}</Chip></svelte:fragment>
            </Sechead>
            <Rows>
              <Line k="ID" v={num(biz.id)} />
              <Line k={t('m.es.safe')} v={money(biz.cash)} tone="gold" />
              <Line k={t('m.es.income')} v={`${money(biz.copiesHour)} / ч`} />
            </Rows>
          </Panel>
        {/if}
        {#if warehouse}
          <Panel>
            <Photo src={warehousePhoto(warehouse.id)} alt={warehouse.name} cls="tall photo" icon="storage" />
            <Sechead title={warehouse.name} divider={false}>
              <svelte:fragment slot="meta"><Chip>{t('m.es.warehouse')}</Chip></svelte:fragment>
            </Sechead>
            <Rows>
              <Line k="ID" v={num(warehouse.id)} />
              <Line k={t('m.es.tax')} v={dmy(warehouse.taxDate)} />
            </Rows>
          </Panel>
        {/if}
      </div>
      <Mgrid style="margin-top:var(--mn-gap)">
        <Metric gold label={t('m.es.safe')} value={money((house?.cash ?? 0) + (biz?.cash ?? 0))} />
        <Metric label={t('m.es.income')} value={money(biz?.copiesHour ?? 0)} smallValue />
        <Metric label={t('m.es.slots')} value={num(house?.maxcars ?? 0)} />
      </Mgrid>
    {/if}
  </Scroll>
{/if}
